import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { CATEGORY_GROUPS } from "@/lib/categories";
import { api, formatErr } from "@/lib/api";
import { Search, Check, X, Plus, Sparkles, Loader2, ShieldCheck } from "lucide-react";

/**
 * Multi-select category picker, grouped, with search + selected chip strip.
 * Plus: lets users suggest a brand-new profession (AI-validated).
 *
 * Props:
 *   value: string[]
 *   onChange: (newList: string[]) => void
 *   max: number (default 6)
 */
export default function CategoryPicker({ value = [], onChange, max = 6 }) {
  const [openGroup, setOpenGroup] = useState(CATEGORY_GROUPS[0].id);
  const [q, setQ] = useState("");
  const [extras, setExtras] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/categories/extras")
      .then(r => { if (alive) setExtras(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Merge built-in groups with community extras by group_id
  const mergedGroups = useMemo(() => {
    const byId = Object.fromEntries(CATEGORY_GROUPS.map(g => [g.id, { ...g, items: [...g.items] }]));
    for (const ex of extras) {
      const gid = byId[ex.group_id] ? ex.group_id : "future";
      if (!byId[gid].items.includes(ex.name)) byId[gid].items.push(ex.name);
    }
    return CATEGORY_GROUPS.map(g => byId[g.id]);
  }, [extras]);

  const filtered = useMemo(() => {
    if (!q.trim()) return null;
    const ql = q.trim().toLowerCase();
    return mergedGroups.map(g => ({
      ...g, items: g.items.filter(c => c.toLowerCase().includes(ql))
    })).filter(g => g.items.length > 0);
  }, [q, mergedGroups]);

  const groups = filtered || mergedGroups;
  const selected = new Set(value);

  const toggle = (c) => {
    if (selected.has(c)) {
      onChange(value.filter(x => x !== c));
    } else if (value.length < max) {
      onChange([...value, c]);
    }
  };

  const noMatches = filtered && filtered.length === 0;

  const onAdded = (name) => {
    setExtras(prev => prev.some(e => e.name.toLowerCase() === name.toLowerCase())
      ? prev : [{ name, group_id: "future" }, ...prev]);
    if (value.length < max && !selected.has(name)) onChange([...value, name]);
    setSuggesting(false);
    setQ("");
  };

  return (
    <div className="card p-5 sm:p-6" data-testid="category-picker">
      <div className="flex items-center gap-2 mb-4">
        <p className="overline flex-1">Your identities · {value.length}/{max}</p>
        {value.length > 0 && <button type="button" className="btn-ghost text-xs" onClick={()=>onChange([])} data-testid="category-clear">Clear</button>}
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="category-selected">
          {value.map(c => (
            <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0A0A] text-white text-sm" data-testid={`cat-selected-${slug(c)}`}>
              {c}
              <button type="button" onClick={()=>toggle(c)} className="hover:opacity-70"><X size={12}/></button>
            </span>
          ))}
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={16}/>
        <input className="input pl-9" placeholder="Search professions" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="category-search"/>
      </div>

      {!filtered && (
        <div className="flex gap-2 flex-wrap mb-4" data-testid="category-tabs">
          {mergedGroups.map(g => (
            <button type="button" key={g.id} onClick={()=>setOpenGroup(g.id)}
              data-testid={`category-tab-${g.id}`}
              className={`pill-link text-xs ${openGroup===g.id?"active":""}`}>
              {g.label}
            </button>
          ))}
        </div>
      )}

      <div className="max-h-72 overflow-y-auto pr-1">
        {groups.map(g => (
          (filtered || g.id === openGroup) && (
            <div key={g.id} className="mb-4" data-testid={`category-group-${g.id}`}>
              {filtered && <p className="text-xs font-medium text-[#8A8A8A] mb-2">{g.label}</p>}
              <div className="flex flex-wrap gap-1.5">
                {g.items.map(c => {
                  const on = selected.has(c);
                  return (
                    <button type="button" key={c}
                      onClick={()=>toggle(c)} data-testid={`cat-${slug(c)}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all
                        ${on ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" :
                              "bg-white text-[#0A0A0A] border-[#E5E5E3] hover:bg-[#F2F2F0]"}`}>
                      {on && <Check size={12}/>}
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          )
        ))}
        {noMatches && (
          <div className="text-center py-6" data-testid="category-no-match">
            <p className="text-sm text-[#8A8A8A] mb-3">No professions match &ldquo;{q}&rdquo;</p>
            <button type="button" onClick={()=>setSuggesting(true)} className="btn-secondary !py-2 !px-4 text-sm"
                    data-testid="category-suggest-open">
              <Plus size={14}/> Suggest &ldquo;{q.trim()}&rdquo; as a profession
            </button>
          </div>
        )}
      </div>

      {/* Persistent "didn't find it" CTA, even when results exist */}
      {!noMatches && (
        <div className="mt-4 pt-4 border-t border-[#E5E5E3] flex items-center justify-between">
          <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1">
            <Sparkles size={12}/> Don&apos;t see your profession?
          </p>
          <button type="button" onClick={()=>setSuggesting(true)} className="btn-ghost text-xs"
                  data-testid="category-suggest-cta">
            Suggest a profession
          </button>
        </div>
      )}

      {suggesting && (
        <SuggestModal
          initial={q.trim()}
          onClose={()=>setSuggesting(false)}
          onAdded={onAdded}
        />
      )}
    </div>
  );
}

function SuggestModal({ initial, onClose, onAdded }) {
  const [name, setName] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true); setErr(""); setInfo("");
    try {
      const { data } = await api.post("/categories/suggest", { name });
      if (!data.ok) {
        setErr(data.reason || "That doesn't look like a real profession.");
        return;
      }
      setInfo(data.duplicate
        ? `"${data.name}" already exists — added to your identities.`
        : `Approved! "${data.name}" is now on Hoomanz.`);
      setTimeout(()=>onAdded(data.name), 700);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  // Rendered via portal so the inner <form> is NOT nested inside a parent <form>
  // (e.g. EditProfile's <form data-testid="edit-profile-form">). React + the browser
  // both treat nested forms as invalid and would bubble the submit to the outer form.
  return createPortal((
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="category-suggest-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="bg-white rounded-3xl p-7 max-w-md w-full">
        <p className="overline mb-1">Suggest a profession</p>
        <h3 className="font-heading text-2xl">Add your craft to Hoomanz.</h3>
        <p className="text-sm text-[#4A4A4A] mt-1 mb-5">
          Every suggestion is reviewed by AI to keep the catalog real. Approved professions appear instantly for everyone.
        </p>

        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Profession name</span>
          <input
            required
            minLength={2}
            maxLength={60}
            className="input mt-1.5"
            placeholder="e.g. Drone Wedding Cinematographer"
            value={name}
            onChange={(e)=>setName(e.target.value)}
            autoFocus
            data-testid="category-suggest-input"
          />
          <span className="text-xs text-[#8A8A8A] mt-1.5 inline-block">2–60 chars · letters, numbers, &amp; &apos; / -</span>
        </label>

        <div className="mt-3 text-xs text-[#4A4A4A] inline-flex items-center gap-1">
          <ShieldCheck size={12}/> Validated for legitimacy · No joke/illegal/adult entries
        </div>

        {err && <p className="text-sm text-[#B84B4B] mt-4" data-testid="category-suggest-error">{err}</p>}
        {info && <p className="text-sm text-[#0A0A0A] mt-4" data-testid="category-suggest-info">{info}</p>}

        <div className="flex gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn-ghost flex-1" data-testid="category-suggest-cancel">Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={busy || name.trim().length < 2} data-testid="category-suggest-submit">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Validating…</> : <><Sparkles size={14}/> Submit for review</>}
          </button>
        </div>
      </form>
    </div>
  ), document.body);
}

function slug(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''); }
